import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Reintenta una llamada async hasta 3 veces (con espera entre intentos) antes de
// darse por vencido — para que un corte de wifi momentáneo no bote todo el envío.
async function conReintento(fn, intentos = 6, esperaMs = 2000) {
  let ultimoError;
  for (let i = 0; i < intentos; i++) {
    try { return await fn(); }
    catch (e) {
      ultimoError = e;
      // Espera creciente entre intentos (2s, 4s, 8s, 16s, 32s) — cubre cortes
      // de conexión más largos, no solo un parpadeo momentáneo.
      if (i < intentos - 1) await new Promise(r => setTimeout(r, esperaMs * Math.pow(2, i)));
    }
  }
  throw ultimoError;
}

// ─────────────────────────────────────────
// Mismas categorías/lógica que el módulo del ERP
// ─────────────────────────────────────────
const CATEGORIAS = {
  insumos:      { label: 'Insumos',      icon: '📦' },
  ingredientes: { label: 'Ingredientes', icon: '🧪' },
  empaques:     { label: 'Empaques',     icon: '🛍️' },
  aseo:         { label: 'Aseo',         icon: '🧹' },
  papeleria:    { label: 'Papelería',    icon: '📄' },
  utensilios:   { label: 'Utensilios',   icon: '🔧' },
};

const SINNO = ['SI/NO hay repuesto', 'SI/NO buen estado', 'SI/NO HAY REPUESTO', 'SI/NO BUEN ESTADO'];
const esSiNo = (unidad) => SINNO.some((s) => unidad?.toUpperCase().includes('SI/NO'));

const estadoColor = {
  borrador:  { bg: '#FEF3C7', color: '#92400E', label: 'Borrador' },
  enviado:   { bg: '#D1FAE5', color: '#065F46', label: 'Enviado ✓' },
  corregido: { bg: '#DBEAFE', color: '#1E40AF', label: 'Corregido' },
};

// ─────────────────────────────────────────
// Input táctil grande (número o SI/NO)
// ─────────────────────────────────────────
function InputCantidad({ valor, onChange, unidad, disabled, placeholder }) {
  if (esSiNo(unidad)) {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {['SI', 'NO'].map((op) => (
          <button
            key={op}
            disabled={disabled}
            onClick={() => !disabled && onChange(op)}
            style={{
              flex: 1, padding: '11px 0', border: '2px solid',
              borderColor: valor === op ? (op === 'SI' ? '#10B981' : '#EF4444') : '#E5E7EB',
              borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: disabled ? 'default' : 'pointer',
              background: valor === op ? (op === 'SI' ? '#D1FAE5' : '#FEE2E2') : 'white',
              color: valor === op ? (op === 'SI' ? '#065F46' : '#991B1B') : '#9CA3AF',
            }}
          >
            {op}
          </button>
        ))}
      </div>
    );
  }
  return (
    <input
      type="number" inputMode="decimal" min="0" value={valor ?? ''} disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      placeholder={placeholder || '0'}
      style={{
        width: '100%', padding: '11px 8px', border: '2px solid #E5E7EB', borderRadius: 10,
        fontSize: 16, outline: 'none', boxSizing: 'border-box', textAlign: 'center',
        background: disabled ? '#F9FAFB' : 'white', color: disabled ? '#9CA3AF' : '#111827',
      }}
    />
  );
}

// Pastilla de selección (mismo patrón que el selector de semana en Horario.jsx)
function Pill({ activo, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      border: `2px solid ${activo ? '#1a1a2e' : '#E5E7EB'}`,
      background: activo ? '#1a1a2e' : 'white',
      color: activo ? 'white' : '#374151',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {children}
    </button>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /inventario
// ─────────────────────────────────────────
export default function Inventario() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia', 'administracion', 'produccion'].includes(usuario?.rol);

  const hoy = format(new Date(), 'yyyy-MM-dd');
  const [tipo, setTipo] = useState('diario');
  const [fecha] = useState(hoy); // en la app siempre se llena para hoy
  const [bodegas, setBodegas] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const [items, setItems] = useState([]);
  const [valores, setValores] = useState({});
  const [registro, setRegistro] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errorPersistente, setErrorPersistente] = useState(null);
  const [vista, setVista] = useState('formulario'); // formulario | historial
  const [historial, setHistorial] = useState([]);
  const [registroDetalle, setRegistroDetalle] = useState(null);

  const esPA = bodegaNombre?.toUpperCase().includes('PARQUE') || bodegaNombre?.toUpperCase().includes('ALEGRA') || false;

  // Punto de venta: si es PDV/empleado, se toma de /app/mi-perfil (no viene en `usuario`)
  useEffect(() => {
    if (esAdmin) {
      API.get('/bodegas').then((r) => {
        const pvs = r.data.filter((b) => b.tipo === 'punto_venta' || !b.tipo);
        setBodegas(pvs);
        if (pvs.length) { setBodegaId(String(pvs[0].id)); setBodegaNombre(pvs[0].nombre); }
      }).catch(() => toast.error('No se pudieron cargar los puntos de venta'));
    } else {
      API.get('/app/mi-perfil').then((r) => {
        if (r.data?.bodega_id) {
          setBodegaId(String(r.data.bodega_id));
          setBodegaNombre(r.data.bodega_nombre || '');
        } else {
          toast.error('No tienes un punto de venta asignado');
        }
      }).catch(() => toast.error('Error cargando tu perfil'));
    }
  }, []);

  const cambiarBodega = (id) => {
    setBodegaId(id);
    const b = bodegas.find((x) => String(x.id) === String(id));
    setBodegaNombre(b?.nombre || '');
  };

  // Items según tipo/bodega
  useEffect(() => {
    if (!bodegaId) return;
    API.get('/inventario/items', { params: { tipo, es_pa: esPA, bodega_id: bodegaId } })
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, [tipo, bodegaId, esPA]);

  // Registro existente de hoy
  const cargarRegistro = useCallback(() => {
    if (!bodegaId) return;
    setCargando(true);
    API.get('/inventario/registro', { params: { bodega_id: bodegaId, tipo, fecha } })
      .then((r) => {
        setRegistro(r.data);
        if (r.data?.detalle) {
          const v = {};
          r.data.detalle.forEach((d) => {
            v[d.item_id] = { cantidad: d.valor_texto ?? d.cantidad, cortesia: d.cortesia, danados: d.danados, observacion: d.observacion };
          });
          setValores(v);
        } else {
          setValores({});
        }
      })
      .catch(() => { setRegistro(null); setValores({}); })
      .finally(() => setCargando(false));
  }, [bodegaId, tipo, fecha]);

  useEffect(() => { cargarRegistro(); }, [cargarRegistro]);

  // Historial (10 últimos)
  useEffect(() => {
    if (!bodegaId || vista !== 'historial') return;
    API.get('/inventario/historial', { params: { bodega_id: bodegaId, tipo, limit: 10 } })
      .then((r) => setHistorial(r.data))
      .catch(() => {});
  }, [bodegaId, tipo, vista]);

  const verRegistro = async (h) => {
    try {
      const r = await API.get('/inventario/registro', { params: { bodega_id: h.bodega_id || bodegaId, tipo: h.tipo, fecha: h.fecha.slice(0, 10) } });
      const detalle = (r.data?.detalle || []).map((d) => ({
        ...d,
        nombre: d.item_nombre || d.nombre || d.item_id,
        categoria: d.categoria || '—',
      }));
      setRegistroDetalle({ ...h, detalle });
    } catch { toast.error('Error cargando el detalle'); }
  };

  const setValor = (itemId, campo, val) => {
    setValores((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [campo]: val } }));
  };

  const bloqueado = registro?.estado === 'enviado' && !esAdmin;

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('No se identificó tu punto de venta'); return; }
    setGuardando(true);
    setErrorPersistente(null);
    try {
      const payload = items.map((item) => ({
        item_id: item.id,
        cantidad: valores[item.id]?.cantidad ?? null,
        cortesia: valores[item.id]?.cortesia ?? 0,
        danados: valores[item.id]?.danados ?? 0,
        observacion: valores[item.id]?.observacion ?? null,
      }));
      await conReintento(() => API.post('/inventario/guardar', { bodega_id: bodegaId, tipo, fecha, items: payload, enviar }));
      if (enviar) {
        // Verificación extra: no confiar solo en que el POST respondió 200 — se
        // vuelve a preguntar al servidor si de verdad quedó como "enviado".
        const verif = await conReintento(() => API.get('/inventario/registro', { params: { bodega_id: bodegaId, tipo, fecha } }));
        if (verif.data?.estado !== 'enviado') throw new Error('No se pudo confirmar el envío en el servidor');
      }
      toast.success(enviar ? '✅ Inventario enviado' : '💾 Guardado como borrador');
      cargarRegistro();
    } catch (e) {
      setErrorPersistente(`No se pudo ${enviar ? 'enviar' : 'guardar'} el inventario después de varios intentos. Revisa tu conexión (wifi/datos) e inténtalo de nuevo. Si sigue sin funcionar, avisa antes de cerrar la app.`);
      toast.error(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const porCategoria = {};
  items.forEach((item) => {
    if (!porCategoria[item.categoria]) porCategoria[item.categoria] = [];
    porCategoria[item.categoria].push(item);
  });

  const mostrarBarra = vista === 'formulario' && !cargando && !bloqueado && Object.keys(porCategoria).length > 0;

  return (
    <div style={{ paddingBottom: mostrarBarra ? 78 : 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>📦 Inventario</h1>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{bodegaNombre || '—'}</div>
        </div>
        {registro?.estado && (
          <span style={{ padding: '5px 12px', borderRadius: 20, fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', background: estadoColor[registro.estado]?.bg, color: estadoColor[registro.estado]?.color }}>
            {estadoColor[registro.estado]?.label}
          </span>
        )}
      </div>

      {/* Selector bodega — solo admin */}
      {esAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
          {bodegas.map((b) => (
            <Pill key={b.id} activo={String(bodegaId) === String(b.id)} onClick={() => cambiarBodega(b.id)}>{b.nombre}</Pill>
          ))}
        </div>
      )}

      {/* Tipo */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <Pill activo={tipo === 'diario'} onClick={() => setTipo('diario')}>📅 Diario</Pill>
        <Pill activo={tipo === 'semanal'} onClick={() => setTipo('semanal')}>📆 Semanal</Pill>
      </div>
      {/* Vista */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Pill activo={vista === 'formulario'} onClick={() => { setVista('formulario'); setRegistroDetalle(null); }}>📝 Llenar</Pill>
        <Pill activo={vista === 'historial'} onClick={() => { setVista('historial'); setRegistroDetalle(null); }}>📋 Historial</Pill>
      </div>

      {vista === 'historial' ? (
        registroDetalle ? (
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>
                  {registroDetalle.tipo === 'diario' ? 'Inventario Diario' : 'Inventario Semanal'}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {(() => { try { return format(new Date(registroDetalle.fecha.slice(0, 10) + 'T12:00'), "d 'de' MMMM yyyy", { locale: es }); } catch { return registroDetalle.fecha; } })()}
                </div>
              </div>
              <button onClick={() => setRegistroDetalle(null)} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>← Volver</button>
            </div>
            {registroDetalle.detalle?.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF', fontSize: 13 }}>Sin detalle registrado</div>
            ) : (
              registroDetalle.detalle.map((d) => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{d.nombre}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{d.categoria}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13 }}>
                    <div style={{ fontWeight: 700 }}>{d.valor_texto ?? d.cantidad ?? '—'}</div>
                    {(d.cortesia > 0 || d.danados > 0) && (
                      <div style={{ fontSize: 11, color: '#6B7280' }}>
                        {d.cortesia > 0 && `Cort: ${d.cortesia} `}{d.danados > 0 && `Dañ: ${d.danados}`}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : historial.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600 }}>Sin registros aún</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historial.map((h) => {
              const ec = estadoColor[h.estado] || estadoColor.borrador;
              return (
                <button key={h.id} onClick={() => verRegistro(h)} style={{ background: 'white', borderRadius: 14, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                      {(() => { try { const d = new Date(h.fecha + 'T12:00'); return isNaN(d) ? h.fecha : format(d, "d 'de' MMM", { locale: es }); } catch { return h.fecha; } })()}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{h.enviado_por_nombre || 'Sin enviar'}</div>
                  </div>
                  <span style={{ padding: '4px 10px', borderRadius: 20, fontWeight: 700, fontSize: 11, background: ec.bg, color: ec.color }}>{ec.label}</span>
                </button>
              );
            })}
          </div>
        )
      ) : cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : (
        <>
          {errorPersistente && (
            <div style={{ background: '#FEF2F2', border: '2px solid #EF4444', borderRadius: 12, padding: '14px 16px', marginBottom: 14, fontSize: 13, color: '#991B1B', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <span>⚠️ {errorPersistente}</span>
              <button onClick={() => setErrorPersistente(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#991B1B', flexShrink: 0 }}>✕</button>
            </div>
          )}

          {bloqueado && (
            <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 12, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#065F46', fontWeight: 600 }}>
              ✅ Ya enviaste este inventario hoy{registro?.enviado_por_nombre ? ` — ${registro.enviado_por_nombre}` : ''}. Solo gerencia puede desbloquearlo.
            </div>
          )}

          {Object.keys(porCategoria).length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
              <div style={{ fontWeight: 600 }}>No hay ítems configurados</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Para este punto de venta</div>
            </div>
          )}

          {Object.entries(porCategoria).map(([cat, catItems]) => {
            const catInfo = CATEGORIAS[cat] || { label: cat, icon: '📋' };
            return (
              <div key={cat} style={{ background: 'white', borderRadius: 14, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: 15 }}>{catInfo.icon}</span>
                  <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.03em', color: '#1a1a2e' }}>{catInfo.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>{catItems.length}</span>
                </div>
                {catItems.map((item) => (
                  <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{item.nombre}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{item.unidad_medida}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div style={{ flex: 2 }}>
                        <InputCantidad valor={valores[item.id]?.cantidad} unidad={item.unidad_medida} disabled={bloqueado} onChange={(v) => setValor(item.id, 'cantidad', v)} />
                      </div>
                      {tipo === 'diario' && (
                        <>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center', marginBottom: 3 }}>Cort.</div>
                            <InputCantidad valor={valores[item.id]?.cortesia} unidad="numero" disabled={bloqueado} placeholder="0" onChange={(v) => setValor(item.id, 'cortesia', v)} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textAlign: 'center', marginBottom: 3 }}>Dañ.</div>
                            <InputCantidad valor={valores[item.id]?.danados} unidad="numero" disabled={bloqueado} placeholder="0" onChange={(v) => setValor(item.id, 'danados', v)} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}

      {/* Barra de acción fija — encima del bottom nav de Layout */}
      {mostrarBarra && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))',
          display: 'flex', gap: 10, padding: '10px 20px', background: 'white',
          borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 90,
        }}>
          <button onClick={() => handleGuardar(false)} disabled={guardando}
            style={{ flex: 1, padding: '13px 0', border: '2px solid #E5E7EB', borderRadius: 12, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#374151' }}>
            {guardando ? '⏳' : '💾 Borrador'}
          </button>
          <button onClick={() => handleGuardar(true)} disabled={guardando}
            style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 12, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            {guardando ? '⏳ Guardando...' : '✅ Enviar inventario'}
          </button>
        </div>
      )}
    </div>
  );
}
